import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { approvalTaskApi } from './api/approvalTaskApi';
import { assetApi } from './api/assetApi';
import { assetDisposalApi } from './api/assetDisposalApi';
import { companyApi } from './api/companyApi';
import { companylocationApi } from './api/companylocation';
import { countryApi } from './api/countryApi';
import { departmentApi } from './api/departmentAPI'; // Add this import
import { depreciationApi } from './api/depreciationApi'; // Add depreciation API import
import { floorPlanApi } from './api/floorPlanApi'; // Add floor plan API import
import { locationApi } from './api/locationApi';
import { locationWorkflowTemplateApi } from './api/locationWorkflowTemplateApi';
import { manufacturerDataApi } from './api/manufacturerApi';
import { modelDataApi } from './api/modelApi';
import { notificationApi } from './api/notificationApi';
import { phoneCodeApi } from './api/phoneCodeApi';
import { roleApi } from './api/roleApi';
import { scheduleApi } from './api/scheduleApi'; // Import the schedule API
import { siteApi } from './api/siteApi';
import { stateApi } from './api/stateApi';
import { userApi } from './api/userApi';
import { workflowApprovalApi } from './api/workflowApprovalApi';
import { workflowAuditTrailApi } from './api/workflowAuditTrailApi';
import { workflowConditionApi } from './api/workflowConditionApi';
import { workflowEntityApi } from './api/workflowEntityApi';
import { workflowEventApi } from './api/workflowEventApi';
import { workflowTemplateApi } from './api/workflowTemplateApi';
import { workflowTransactionApi } from './api/workflowTransactionApi';
import { uuidResolverApi } from './api/uuidResolverApi';
import { customFormsApi } from './api/customFormsApi';
import { tableConfigApi } from './api/tableConfigApi';
import { dashboardApi } from './api/dashboardApi';
import assetCreateReducer from './slices/assetCreateSlice';
import assetTransferReducer from './slices/assetTransferSlice';
import authReducer from './slices/authSlice';
import assetDisposalReducer from './slices/assetDisposalSlice';
import { errorMiddleware } from './middleware/errorMiddleware';
import { movementHistoryApi } from './api/movementHistoryApi';

// Configure store
export const store = configureStore({
  reducer: {
    auth: authReducer,
    assetCreate: assetCreateReducer,
    assetTransfer: assetTransferReducer,
    assetDisposal: assetDisposalReducer,
    [assetApi.reducerPath]: assetApi.reducer,
    [countryApi.reducerPath]: countryApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [stateApi.reducerPath]: stateApi.reducer,
    [siteApi.reducerPath]: siteApi.reducer,
    [locationApi.reducerPath]: locationApi.reducer,
    [roleApi.reducerPath]: roleApi.reducer,
    [approvalTaskApi.reducerPath]: approvalTaskApi.reducer,
    [workflowApprovalApi.reducerPath]: workflowApprovalApi.reducer,
    [workflowAuditTrailApi.reducerPath]: workflowAuditTrailApi.reducer,
    [workflowConditionApi.reducerPath]: workflowConditionApi.reducer,
    [workflowEntityApi.reducerPath]: workflowEntityApi.reducer,
    [workflowEventApi.reducerPath]: workflowEventApi.reducer,
    [workflowTemplateApi.reducerPath]: workflowTemplateApi.reducer,
    [workflowTransactionApi.reducerPath]: workflowTransactionApi.reducer,
    [notificationApi.reducerPath]: notificationApi.reducer,
    [locationWorkflowTemplateApi.reducerPath]: locationWorkflowTemplateApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [departmentApi.reducerPath]: departmentApi.reducer,
    [companylocationApi.reducerPath]: companylocationApi.reducer,
    [phoneCodeApi.reducerPath]: phoneCodeApi.reducer,
    [manufacturerDataApi.reducerPath]: manufacturerDataApi.reducer,
    [modelDataApi.reducerPath]: modelDataApi.reducer,
    [scheduleApi.reducerPath]: scheduleApi.reducer, // Add schedule API reducer
    [depreciationApi.reducerPath]: depreciationApi.reducer, // Add depreciation API reducer
    [floorPlanApi.reducerPath]: floorPlanApi.reducer, // Add floor plan API reducer
    [assetDisposalApi.reducerPath]: assetDisposalApi.reducer,
    [uuidResolverApi.reducerPath]: uuidResolverApi.reducer,
    [customFormsApi.reducerPath]: customFormsApi.reducer,
    [tableConfigApi.reducerPath]: tableConfigApi.reducer,
    [dashboardApi.reducerPath]: dashboardApi.reducer,
    [movementHistoryApi.reducerPath]: movementHistoryApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      assetApi.middleware,
      countryApi.middleware,
      companyApi.middleware,
      stateApi.middleware,
      siteApi.middleware,
      locationApi.middleware,
      roleApi.middleware,
      approvalTaskApi.middleware,
      workflowApprovalApi.middleware,
      workflowAuditTrailApi.middleware,
      workflowConditionApi.middleware,
      workflowEntityApi.middleware,
      workflowEventApi.middleware,
      workflowTemplateApi.middleware,
      workflowTransactionApi.middleware,
      notificationApi.middleware,
      locationWorkflowTemplateApi.middleware,
      userApi.middleware,
      departmentApi.middleware,
      companylocationApi.middleware,
      phoneCodeApi.middleware,
      manufacturerDataApi.middleware,
      modelDataApi.middleware,
      scheduleApi.middleware, // Add schedule API middleware
      depreciationApi.middleware, // Add depreciation API middleware
      floorPlanApi.middleware, // Add floor plan API middleware
      assetDisposalApi.middleware,
      uuidResolverApi.middleware,
      customFormsApi.middleware,
      tableConfigApi.middleware,
      dashboardApi.middleware,
      movementHistoryApi.middleware,
      errorMiddleware // This should always kept at last
    ),
  devTools: import.meta.env.MODE !== 'production',
});

// Enable refetchOnFocus and refetchOnReconnect
setupListeners(store.dispatch);

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;